/* eslint-disable */

import { Options, Chart as ChartApi, merge, SeriesFlagsOptions } from 'highcharts'
import Highcharts from 'highcharts/highstock'
import { memo, useCallback, useEffect, useMemo } from 'react'
import { HighchartsReact } from 'highcharts-react-official'

import classNames from 'classnames'

import 'highcharts/modules/accessibility'
import 'highcharts/modules/exporting'
import 'highcharts/modules/export-data'
import 'highcharts/indicators/ema'
import 'highcharts/indicators/indicators'

import { ICustomChartOptions } from '~/types/chart'

import { formatExportFileName } from '~/utils/string'
import { cohortedAnalyticsDefaultOptions, defaultOptions, SECONDS_IN_DAY } from './options'

import { ISeries } from '~/types/series'
import { formatTooltipValue, tooltipUtils } from '~/utils/chart'

import './styles.scss'

export interface Props {
  constructorType?: string
  className?: string
  series: ISeries[]
  eventSeries?: SeriesFlagsOptions
  options?: Options & ICustomChartOptions
  getHighchartsRef?: (ref: typeof Highcharts) => void
  getChartApi?: (chartApi: ChartApi) => void
}

const containerProps = { style: { height: '99%', width: '100%' } }

const Chart = ({
  className,
  constructorType = 'stockChart',
  series,
  eventSeries,
  options,
  getHighchartsRef,
  getChartApi,
}: Props) => {
  /** Local state */

  const chartClasses = classNames('chart-component', className)

  const chartOptions = useMemo(() => {
    const { defaultRange, autoScrollbar, seriesFormatter, tooltip } = options || {}

    const _series = seriesFormatter ? [...seriesFormatter(series)] : [...series]

    if (eventSeries) {
      _series.push(eventSeries)
    }

    const customOptions: Highcharts.Options = {
      exporting: {
        csv: {
          columnHeaderFormatter: (item: Highcharts.Axis) => {
            if (!item || item instanceof Highcharts.Axis) {
              return options?.exportColumnHeaderName || 'Date'
            }
            return false
          },
          dateFormat: '%b %d, %Y',
        },
        enabled: false,
        filename: formatExportFileName(options?.exportFileName || 'export'),
      },
      plotOptions: {
        series: {
          dataGrouping: {
            enabled: false,
          },
        },
      },
      series: _series,
      tooltip: {
        pointFormatter: function () {
          if (typeof this.y !== 'undefined') {
            const { color, series, y } = this

            const label = tooltipUtils.getLabel({ color, value: series.name })
            const value = tooltipUtils.getValue({
              value: tooltip?.valueFormatter ? tooltip?.valueFormatter(y) : formatTooltipValue(y),
            })

            return `<div>${label} : ${value}</div>`
          }
          return ''
        },
      },
      xAxis: {
        range: defaultRange ? defaultRange * SECONDS_IN_DAY : undefined,
        scrollbar: {
          enabled: autoScrollbar ? autoScrollbar(series) : undefined,
        },
      },
    }

    const mergedOptions = merge(
      window.location.pathname?.includes('/ca/') ? cohortedAnalyticsDefaultOptions : defaultOptions,
      customOptions,
      options
    ) as Highcharts.Options

    return mergedOptions
  }, [eventSeries, options])

  /** Effects */

  useEffect(() => {
    if (getHighchartsRef) {
      getHighchartsRef(Highcharts)
    }
  }, [Highcharts])

  /** Handlers */

  const handleChartCallback = useCallback(
    (chartApi: ChartApi) => {
      getChartApi && getChartApi(chartApi)
    },
    [getChartApi]
  )

  return (
    <div className={chartClasses}>
      <HighchartsReact
        containerProps={containerProps}
        highcharts={Highcharts}
        constructorType={constructorType}
        options={chartOptions}
        callback={handleChartCallback}
      />
    </div>
  )
}

export default memo(Chart)
